import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ImageLightbox from "@/components/ImageLightbox";
import {
  Plus,
  Truck,
  Motorcycle,
  Car,
  Bicycle,
  PencilSimple,
  Trash,
  CheckCircle,
  Prohibit,
  ShieldCheck,
  ShieldWarning,
  Hourglass,
  XCircle,
  User as UserIcon,
  ListChecks,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const VEHICLE_OPTIONS = [
  { v: "motorcycle", label: "Motorcycle", Icon: Motorcycle },
  { v: "van",        label: "Van",        Icon: Truck },
  { v: "truck",      label: "Truck",      Icon: Truck },
  { v: "car",        label: "Car",        Icon: Car },
  { v: "bicycle",    label: "Bicycle",    Icon: Bicycle },
];

const KG_TO_LB = 2.20462;

const EMPTY = {
  id: null,
  name: "",
  phone: "",
  base_address: "",
  base_lat: null,
  base_lng: null,
  vehicle_type: "van",
  license_plate: "",
  license_number: "",
  trn: "",
  capacity_kg: "",
  is_active: true,
  company_id: "",
};

function vehicleIcon(type) {
  return (VEHICLE_OPTIONS.find((v) => v.v === type) || VEHICLE_OPTIONS[1]).Icon;
}

export default function Drivers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [capacityInput, setCapacityInput] = useState(""); // raw value in the chosen unit
  const [capacityUnit, setCapacityUnit] = useState("kg");
  const [filterCompany, setFilterCompany] = useState("all");
  const [activeTab, setActiveTab] = useState("all"); // all | verified | unverified | email_pending | rejected
  const [lightbox, setLightbox] = useState(null); // { src, title } or null
  const [jobsDriver, setJobsDriver] = useState(null); // driver whose jobs we're viewing
  const [jobsTab, setJobsTab] = useState("active"); // active | history | all
  const [jobsRows, setJobsRows] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  // Driver thumbnails use RELATIVE paths (handled by api.js / direct fetch) so
  // we never embed the Emergent stage hostname in <img src>; same-origin
  // requests automatically send the session cookie required for protected
  // media (selfies, license/vehicle photos).

  const load = async () => {
    const params = {};
    if (user.role === "super_admin" && filterCompany !== "all") {
      params.company_id = filterCompany;
    }
    try {
      const { data } = await api.get("/drivers", { params });
      setRows(data);
    } catch (e) {
      toast.error("Failed to load drivers");
    }
  };

  useEffect(() => {
    if (user.role === "super_admin") {
      api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    }
  }, [user.role]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCompany]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = { ...form };
    if (user.role !== "super_admin") {
      delete payload.company_id;
    } else if (!form.id && !payload.company_id) {
      toast.error("Select a company"); return;
    }
    // Capacity: convert from chosen unit to kg before persisting (always stored as kg)
    if (capacityInput && !isNaN(parseFloat(capacityInput))) {
      const v = parseFloat(capacityInput);
      payload.capacity_kg = capacityUnit === "lb" ? v / KG_TO_LB : v;
    } else {
      delete payload.capacity_kg;
    }
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" || payload[k] === null) delete payload[k];
    });
    if (form.id) delete payload.id;
    try {
      if (form.id) {
        await api.patch(`/drivers/${form.id}`, payload);
        toast.success("Driver updated");
      } else {
        await api.post("/drivers", payload);
        toast.success("Driver added");
      }
      setOpen(false);
      setForm(EMPTY);
      setCapacityInput("");
      setCapacityUnit("kg");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (d) => {
    if (!confirm(`Remove driver "${d.name}"? Active packages will be unassigned.`)) return;
    try {
      await api.delete(`/drivers/${d.id}`);
      toast.success("Driver removed");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const toggleActive = async (d) => {
    try {
      await api.patch(`/drivers/${d.id}`, { is_active: !d.is_active });
      toast.success(`${d.name} ${!d.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const startEdit = (d) => {
    setForm({
      id: d.id,
      name: d.name || "",
      phone: d.phone || "",
      base_address: d.base_address || "",
      base_lat: d.base_lat ?? null,
      base_lng: d.base_lng ?? null,
      vehicle_type: d.vehicle_type || "van",
      license_plate: d.license_plate || "",
      license_number: d.license_number || "",
      trn: d.trn || "",
      capacity_kg: d.capacity_kg ?? "",
      is_active: d.is_active !== false,
      company_id: d.company_id || "",
    });
    // Surface capacity in kg by default when editing
    setCapacityInput(d.capacity_kg ? String(Number(d.capacity_kg).toFixed(2)) : "");
    setCapacityUnit("kg");
    setOpen(true);
  };

  const startNew = () => {
    setForm(EMPTY);
    setCapacityInput("");
    setCapacityUnit("kg");
    setOpen(true);
  };

  const companyName = (id) => {
    if (id === "zipexpress-network") return "ZipExpress Network";
    return companies.find((c) => c.id === id)?.name || "—";
  };

  const verifyDriver = async (d) => {
    if (!confirm(`Approve ${d.name} as a verified ZipExpress driver?`)) return;
    try {
      await api.post(`/drivers/${d.id}/verify`);
      toast.success(`${d.name} verified — login credentials emailed`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const rejectDriver = async (d) => {
    const reason = prompt(`Reject ${d.name}? Provide a reason:`);
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/drivers/${d.id}/reject`, { reason: reason.trim() });
      toast.success(`${d.name} rejected`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const viewLicense = (d, side) => {
    setLightbox({
      src: `/api/drivers/${d.id}/license/${side}`,
      title: `${d.name} · License ${side}`,
      testid: `driver-license-${side}-lightbox`,
    });
  };
  const viewSelfie = (d) => {
    setLightbox({
      src: `/api/drivers/${d.id}/selfie`,
      title: `${d.name} · Selfie`,
      testid: "driver-selfie-lightbox",
    });
  };
  // Vehicle photo lightbox — also captures GPS + timestamp for the caption
  const viewVehicle = (d, side) => {
    const lat = side === "front" ? d.vehicle_front_lat : d.vehicle_back_lat;
    const lng = side === "front" ? d.vehicle_front_lng : d.vehicle_back_lng;
    const takenAt = side === "front" ? d.vehicle_front_taken_at : d.vehicle_back_taken_at;
    setLightbox({
      src: `/api/drivers/${d.id}/vehicle/${side}`,
      title: `${d.name} · Vehicle ${side}`,
      testid: `driver-vehicle-${side}-lightbox`,
      meta: {
        timestamp: takenAt,
        lat,
        lng,
        mapTitle: `Where ${side} photo was taken`,
      },
    });
  };

  const openJobs = async (d) => {
    setJobsDriver(d);
    setJobsTab("active");
    setJobsRows([]);
  };

  // Load jobs whenever the dialog is open or tab changes
  useEffect(() => {
    if (!jobsDriver) return;
    setJobsLoading(true);
    api.get(`/drivers/${jobsDriver.id}/jobs`, { params: { status: jobsTab } })
      .then(({ data }) => setJobsRows(data))
      .catch(() => toast.error("Failed to load jobs"))
      .finally(() => setJobsLoading(false));
  }, [jobsDriver, jobsTab]);

  // Tab counts + filtering
  const tabCounts = rows.reduce((acc, d) => {
    const s = d.verification_status || "verified";
    acc[s] = (acc[s] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});
  const tabbed = activeTab === "all" ? rows : rows.filter((d) => (d.verification_status || "verified") === activeTab);
  const isSuper = user.role === "super_admin";

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="drivers-page">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
            Last-Mile
          </div>
          <h1
            className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1"
            style={{ fontFamily: "Work Sans" }}
          >
            Delivery Drivers
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Ground packages are auto-assigned to the nearest active driver based on the recipient address.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user.role === "super_admin" && (
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger className="w-56" data-testid="drivers-company-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
            onClick={startNew}
            data-testid="new-driver-btn"
          >
            <Plus size={15} weight="bold" className="mr-1.5" /> New Driver
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        {isSuper && (
          <div className="flex flex-wrap gap-1 px-3 pt-3 pb-0 border-b border-zinc-100" data-testid="driver-tabs">
            {[
              { v: "all", label: "All" },
              { v: "unverified", label: "Awaiting review", Icon: ShieldWarning, color: "amber" },
              { v: "verified", label: "Verified", Icon: ShieldCheck, color: "emerald" },
              { v: "email_pending", label: "Email pending", Icon: Hourglass, color: "zinc" },
              { v: "rejected", label: "Rejected", Icon: XCircle, color: "red" },
            ].map((t) => {
              const active = activeTab === t.v;
              const colorMap = {
                amber: "bg-amber-50 text-amber-700 border-amber-200",
                emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
                zinc: "bg-zinc-100 text-zinc-700 border-zinc-200",
                red: "bg-red-50 text-red-700 border-red-200",
              };
              return (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setActiveTab(t.v)}
                  className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-t-md border-b-2 transition-colors flex items-center gap-1.5 ${active ? "border-[#002BF6] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}
                  data-testid={`driver-tab-${t.v}`}
                >
                  {t.Icon && <t.Icon size={12} weight="bold" />}
                  {t.label}
                  <span className={`text-[10px] rounded-full px-1.5 py-0.5 border ${active ? "bg-[#002BF6]/10 text-[#002BF6] border-[#002BF6]/20" : "border-zinc-200 text-zinc-500"}`}>
                    {tabCounts[t.v] || 0}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <table className="w-full text-sm min-w-[820px]" data-testid="drivers-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Driver</th>
              <th className="px-5 py-3 font-semibold">Vehicle</th>
              <th className="px-5 py-3 font-semibold">Capacity</th>
              <th className="px-5 py-3 font-semibold">Base</th>
              <th className="px-5 py-3 font-semibold">Phone</th>
              <th className="px-5 py-3 font-semibold">Active load</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              {(isSuper || user.role === "company_admin") && <th className="px-5 py-3 font-semibold">License #</th>}
              {isSuper && <th className="px-5 py-3 font-semibold">TRN #</th>}
              {isSuper && <th className="px-5 py-3 font-semibold">Company</th>}
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tabbed.length === 0 ? (
              <tr>
                <td colSpan={isSuper ? 10 : (user.role === "company_admin" ? 9 : 8)} className="px-5 py-12 text-center text-zinc-500">
                  <Truck size={32} className="mx-auto mb-2 text-zinc-300" />
                  No drivers in this view.
                </td>
              </tr>
            ) : tabbed.map((d) => {
              const VIcon = vehicleIcon(d.vehicle_type);
              const vs = d.verification_status || "verified";
              return (
                <tr key={d.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`driver-row-${d.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {d.has_selfie ? (
                        <button
                          type="button"
                          onClick={() => viewSelfie(d)}
                          className="shrink-0 group relative rounded-full focus:outline-none focus:ring-2 focus:ring-[#002BF6]/40"
                          title="Click to enlarge selfie"
                          data-testid={`driver-selfie-zoom-${d.id}`}
                        >
                          <img
                            src={`/api/drivers/${d.id}/selfie`}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover border border-zinc-200 group-hover:border-[#002BF6] transition-colors"
                            data-testid={`driver-selfie-${d.id}`}
                          />
                        </button>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
                          <UserIcon size={16} weight="bold" className="text-zinc-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-900 truncate">{d.name}</div>
                        {d.email && (user.role === "super_admin" || user.role === "company_admin") && (
                          <div className="text-[11px] text-zinc-500 truncate" data-testid={`driver-email-${d.id}`}>{d.email}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 text-sm text-zinc-700 capitalize">
                      <VIcon size={14} weight="bold" className="text-zinc-500" />
                      {d.vehicle_type}
                    </span>
                  </td>
                  <td className="px-5 py-4" data-testid={`driver-capacity-${d.id}`}>
                    {d.capacity_kg ? (
                      <>
                        <div className="text-sm text-zinc-900">{Number(d.capacity_kg).toFixed(2)} kg</div>
                        <div className="text-[11px] text-zinc-500">{(Number(d.capacity_kg) * 2.20462).toFixed(2)} lb</div>
                      </>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-zinc-700 max-w-[260px] truncate" title={d.base_address || ""}>
                    {d.base_address || "—"}
                    {(d.base_lat != null && d.base_lng != null) && (
                      <div className="text-[10px] text-emerald-600 font-mono">
                        ● geocoded
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-zinc-700">{d.phone || "—"}</td>
                  <td className="px-5 py-4 text-zinc-700">
                    {(() => {
                      const pkgN = d.active_package_count || 0;
                      const pickN = d.active_pickup_count || 0;
                      const total = pkgN + pickN;
                      if (total === 0) {
                        return <span className="text-zinc-400 font-mono" data-testid={`driver-load-${d.id}`}>0</span>;
                      }
                      return (
                        <div className="flex flex-col gap-0.5" data-testid={`driver-load-${d.id}`}>
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                            <span className="font-mono text-sm">{pkgN}</span>
                            <span className="text-zinc-500 font-normal">{pkgN === 1 ? "delivery" : "deliveries"}</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700">
                            <span className="font-mono text-sm">{pickN}</span>
                            <span className="text-zinc-500 font-normal">{pickN === 1 ? "pickup" : "pickups"}</span>
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-col gap-1.5 items-start">
                      {vs === "verified" && (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`driver-vs-${d.id}`}>
                          <ShieldCheck size={11} weight="bold" /> Verified
                        </span>
                      )}
                      {vs === "unverified" && (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-testid={`driver-vs-${d.id}`}>
                          <ShieldWarning size={11} weight="bold" /> Awaiting review
                        </span>
                      )}
                      {vs === "email_pending" && (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200" data-testid={`driver-vs-${d.id}`}>
                          <Hourglass size={11} weight="bold" /> Email pending
                        </span>
                      )}
                      {vs === "rejected" && (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border border-red-200" data-testid={`driver-vs-${d.id}`} title={d.rejected_reason || ""}>
                          <XCircle size={11} weight="bold" /> Rejected
                        </span>
                      )}
                      {d.is_active ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700" data-testid={`driver-active-${d.id}`}>
                          <CheckCircle size={10} weight="bold" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500" data-testid={`driver-inactive-${d.id}`}>
                          <Prohibit size={10} weight="bold" /> Inactive
                        </span>
                      )}
                      {/* Vehicle photo freshness badge — admins see when on-file
                          photos are missing or >90 days old. */}
                      {d.vehicle_photos_expired ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200"
                          title="Vehicle photos missing or older than 90 days. Ask the driver to refresh in their portal."
                          data-testid={`driver-vehicle-photos-expired-${d.id}`}
                        >
                          <Truck size={10} weight="bold" /> Photos expired
                        </span>
                      ) : (d.vehicle_photos_days_remaining ?? 999) <= 14 && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                          title={`Vehicle photos expire in ${d.vehicle_photos_days_remaining} days`}
                          data-testid={`driver-vehicle-photos-due-${d.id}`}
                        >
                          <Truck size={10} weight="bold" /> Photos due ({d.vehicle_photos_days_remaining}d)
                        </span>
                      )}
                    </div>
                  </td>
                  {(isSuper || user.role === "company_admin") && (
                    <td className="px-5 py-4 text-zinc-700 font-mono text-xs" data-testid={`driver-license-col-${d.id}`}>
                      {d.license_number ? d.license_number : <span className="text-zinc-300">—</span>}
                    </td>
                  )}
                  {isSuper && (
                    <td className="px-5 py-4 text-zinc-700 font-mono text-xs" data-testid={`driver-trn-${d.id}`}>
                      {d.trn ? d.trn : <span className="text-zinc-300">—</span>}
                    </td>
                  )}
                  {isSuper && (
                    <td className="px-5 py-4 text-zinc-700">{companyName(d.company_id)}</td>
                  )}
                  <td className="px-5 py-4 text-right">
                    {isSuper && vs === "unverified" && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => verifyDriver(d)} data-testid={`driver-verify-${d.id}`} className="text-emerald-700 hover:bg-emerald-50">
                          <ShieldCheck size={14} className="mr-1" /> Verify
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => rejectDriver(d)} data-testid={`driver-reject-${d.id}`} className="text-red-600 hover:bg-red-50">
                          <XCircle size={14} className="mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {isSuper && d.has_license_front && (
                      <Button variant="ghost" size="sm" onClick={() => viewLicense(d, "front")} data-testid={`driver-license-front-${d.id}`} title="View license front" className="text-zinc-600 font-mono">
                        ID F
                      </Button>
                    )}
                    {isSuper && d.has_license_back && (
                      <Button variant="ghost" size="sm" onClick={() => viewLicense(d, "back")} data-testid={`driver-license-back-${d.id}`} title="View license back" className="text-zinc-600 font-mono">
                        ID B
                      </Button>
                    )}
                    {isSuper && d.has_vehicle_front && (
                      <Button variant="ghost" size="sm" onClick={() => viewVehicle(d, "front")} data-testid={`driver-vehicle-front-${d.id}`} title="View vehicle front + GPS" className="text-sky-700 font-mono">
                        VEH F
                      </Button>
                    )}
                    {isSuper && d.has_vehicle_back && (
                      <Button variant="ghost" size="sm" onClick={() => viewVehicle(d, "back")} data-testid={`driver-vehicle-back-${d.id}`} title="View vehicle back + GPS" className="text-sky-700 font-mono">
                        VEH B
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openJobs(d)} data-testid={`driver-jobs-${d.id}`} title="View jobs">
                      <ListChecks size={14} className="text-zinc-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(d)} data-testid={`driver-toggle-${d.id}`} title={d.is_active ? "Deactivate" : "Activate"}>
                      {d.is_active ? <Prohibit size={14} className="text-amber-600" /> : <CheckCircle size={14} className="text-emerald-600" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(d)} data-testid={`driver-edit-${d.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(d)} data-testid={`driver-delete-${d.id}`} className="text-red-600 hover:text-red-700">
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-2xl p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
          data-testid="driver-dialog"
        >
          <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle>{form.id ? "Edit driver" : "Add driver"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto px-5 sm:px-6 py-4 flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Full name</Label>
                <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="driver-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Phone</Label>
                <Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="driver-phone" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Base address {form.base_lat != null && <span className="text-emerald-600 normal-case font-normal ml-1">● geocoded</span>}
              </Label>
              <AddressAutocomplete
                value={form.base_address}
                onChange={(v) => setForm({ ...form, base_address: v })}
                onLocationChange={(lat, lng) => setForm((f) => ({ ...f, base_lat: lat, base_lng: lng }))}
                rows={2}
                className="mt-1.5"
                dataTestId="driver-address"
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                Select a suggestion to capture lat/lng for distance-based auto-assignment.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Vehicle</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="driver-vehicle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VEHICLE_OPTIONS.map((v) => (
                      <SelectItem key={v.v} value={v.v}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">License plate</Label>
                <Input className="mt-1.5" value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} data-testid="driver-plate" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">License #</Label>
                <Input className="mt-1.5" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} data-testid="driver-license" />
              </div>
            </div>

            {/* TRN (Tax Registration Number) — super_admin only */}
            {isSuper && (
              <div data-testid="driver-trn-field">
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600 flex items-center gap-1.5">
                  TRN #
                  <span className="text-[10px] font-medium normal-case tracking-normal text-zinc-400">Tax Registration Number · super-admin only</span>
                </Label>
                <Input
                  className="mt-1.5 font-mono"
                  value={form.trn}
                  onChange={(e) => setForm({ ...form, trn: e.target.value })}
                  placeholder="e.g. 123-456-789"
                  inputMode="numeric"
                  data-testid="driver-trn-input"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Used for tax reporting. Only super admins can view or edit this value.</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Capacity</Label>
                  <div className="inline-flex items-center text-[11px] rounded overflow-hidden border border-zinc-300 shrink-0">
                    <button
                      type="button"
                      onClick={() => setCapacityUnit("kg")}
                      className={`px-2.5 py-1 font-semibold ${capacityUnit === "kg" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`}
                      data-testid="driver-cap-unit-kg"
                    >KG</button>
                    <button
                      type="button"
                      onClick={() => setCapacityUnit("lb")}
                      className={`px-2.5 py-1 font-semibold ${capacityUnit === "lb" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`}
                      data-testid="driver-cap-unit-lb"
                    >LB</button>
                  </div>
                </div>
                <Input
                  type="number"
                  step="0.1"
                  className="mt-1.5"
                  value={capacityInput}
                  onChange={(e) => setCapacityInput(e.target.value)}
                  placeholder={capacityUnit === "kg" ? "e.g. 250" : "e.g. 551"}
                  data-testid="driver-capacity"
                />
                {capacityInput && !isNaN(parseFloat(capacityInput)) && (
                  <div className="text-[11px] text-zinc-500 mt-1" data-testid="driver-capacity-hint">
                    {capacityUnit === "kg"
                      ? `≈ ${(parseFloat(capacityInput) * KG_TO_LB).toFixed(2)} lb`
                      : `≈ ${(parseFloat(capacityInput) / KG_TO_LB).toFixed(2)} kg`}
                  </div>
                )}
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded"
                    data-testid="driver-is-active"
                  />
                  <span className="text-sm">Active (eligible for auto-assignment)</span>
                </label>
              </div>
            </div>

            {user.role === "super_admin" && !form.id && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Company</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="driver-company"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-driver-btn">
              {form.id ? "Save changes" : "Add driver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Driver jobs viewer */}
      <Dialog open={!!jobsDriver} onOpenChange={(v) => { if (!v) setJobsDriver(null); }}>
        <DialogContent
          className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-3xl p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
          data-testid="driver-jobs-dialog"
        >
          <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle>Jobs · {jobsDriver?.name}</DialogTitle>
          </DialogHeader>
          <div className="px-5 sm:px-6 pt-3 pb-2 border-b border-zinc-100 shrink-0 flex gap-1">
            {[
              { v: "active", label: "Active" },
              { v: "history", label: "History" },
              { v: "all", label: "All" },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => setJobsTab(t.v)}
                data-testid={`driver-jobs-tab-${t.v}`}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${
                  jobsTab === t.v ? "bg-[#002BF6] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="overflow-y-auto px-5 sm:px-6 py-4 flex-1">
            {jobsLoading ? (
              <div className="text-center text-sm text-zinc-500 py-12">Loading...</div>
            ) : jobsRows.length === 0 ? (
              <div className="text-center text-sm text-zinc-500 py-12" data-testid="driver-jobs-empty">
                No jobs in this view.
              </div>
            ) : (
              <div className="space-y-2" data-testid="driver-jobs-list">
                {jobsRows.map((p) => (
                  <div key={p.id} className="rounded-md border border-zinc-200 px-3 py-2.5 hover:bg-zinc-50" data-testid={`driver-job-row-${p.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-xs font-mono bg-zinc-100 text-zinc-800 rounded px-1.5 py-0.5">{p.tracking_number}</code>
                          <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border border-zinc-200 text-zinc-700 bg-zinc-50">
                            {(p.status || "").replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-zinc-900 mt-1 truncate">{p.recipient_name || "—"}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{p.recipient_address || ""}</div>
                      </div>
                      {p.weight_kg ? (
                        <div className="text-right shrink-0">
                          <div className="text-xs text-zinc-700">{Number(p.weight_kg).toFixed(2)} kg</div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0">
            <Button variant="outline" onClick={() => setJobsDriver(null)} data-testid="driver-jobs-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageLightbox
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        src={lightbox?.src}
        alt={lightbox?.title || "image"}
        title={lightbox?.title}
        testid={lightbox?.testid || "driver-image-lightbox"}
        meta={lightbox?.meta}
      />
    </div>
  );
}
